/**
 * SigningModal — a full-screen, DocuSign-style signing experience launched from a journey card.
 *
 * The actual document IS the content (rendered inline via PDF.js) — no paraphrase. A "Sign here"
 * field is placed on the real acceptance/signature line (located by text-search of the PDF), so the
 * signer sees exactly where their signature lands. They adopt a signature (draw or type), it renders
 * in that field on the document, then confirm. The server bakes the signature at the same spot.
 *
 * Props:
 *   pdfUrl          — the document to review + sign
 *   title, subtitle — header text
 *   anchorQueries   — strings to locate the signature line in the PDF text (first match wins)
 *   signerLine      — on a joint (two-signer) proposal, which physical "Client -" line (1 or 2)
 *                     belongs to the person currently signing (api/journey.js's
 *                     resolveMySignerLine). Omit/1 for the default — the single-signer case, and
 *                     the safe fallback whenever the viewer couldn't be matched to a line.
 *   packageOptions  — optional [{ key, label, fee }] for a proposal suite choice (required to
 *                     finish). Rendered as a picker only when there are 2+ — a single offered
 *                     package auto-selects with no UI, nothing to choose. Ignored when
 *                     lockedPackageLabel is set.
 *   lockedPackageLabel — on a joint (two-signer) BS proposal genuinely offering 2+ packages, the
 *                     suite the OTHER (first) required signer already chose. When set, no picker
 *                     is shown — this signer instead sees the locked choice and must check an
 *                     explicit confirmation before finishing (required to finish, same as
 *                     packageOptions). Mutually exclusive with packageOptions in practice — the
 *                     caller (ProposalReviewEntry.jsx) passes one or the other, never both.
 *   consentLabel    — ESIGN consent checkbox text
 *   busy            — submit in flight
 *   onClose()       — dismiss without signing
 *   onSubmit({ signerName, signatureDataUrl, method, font, consent, package }) — finish. `package`
 *     is the chosen OR confirmed suite label either way — the caller doesn't need to distinguish;
 *     api/journey-respond.js derives "selected" vs "confirmed" itself from signing order.
 */
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { loadPdfjs } from '../lib/pdfjs.js'
import JourneySignaturePad from './JourneySignaturePad.jsx'

export default function SigningModal({ pdfUrl, title, subtitle, anchorQueries = [], signerLine = 1, packageOptions = null, lockedPackageLabel = null, consentLabel, busy, onClose, onSubmit }) {
  const scrollRef = useRef(null)
  const pagesRef = useRef(null)
  const [status, setStatus] = useState('loading')   // loading | ready | error
  const [fields, setFields] = useState(null)         // { signature, date?, print? } each { wrap, left, top, w, h }
  const [sig, setSig] = useState({ name: '', dataUrl: '', method: 'draw', font: null })
  const [agree, setAgree] = useState(false)
  const [emailCopy, setEmailCopy] = useState(false)
  const [copyEmail, setCopyEmail] = useState('')
  const [pkg, setPkg] = useState(packageOptions && packageOptions.length === 1 ? packageOptions[0].key : '')
  const [confirmedPkg, setConfirmedPkg] = useState(false)
  const [winW, setWinW] = useState(typeof window !== 'undefined' ? window.innerWidth : 1280)
  const [started, setStarted] = useState(false)   // "Ready to sign" gate before the input fields show

  useEffect(() => { const r = () => setWinW(window.innerWidth); window.addEventListener('resize', r); return () => window.removeEventListener('resize', r) }, [])
  useEffect(() => { document.body.style.overflow = 'hidden'; return () => { document.body.style.overflow = '' } }, [])

  // Render the PDF inline and locate the signature line (text-search) to place the Sign-here field.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const pdfjsLib = await loadPdfjs()
        if (!pdfjsLib || cancelled) { setStatus('error'); return }
        const host = pagesRef.current; if (!host) return
        host.innerHTML = ''
        const doc = await pdfjsLib.getDocument(pdfUrl).promise
        const dpr = Math.min(window.devicePixelRatio || 1, window.innerWidth < 700 ? 1.5 : 2)
        // The rendered page column is proportional to the CURRENT viewer's available width by
        // design — a phone can't show an 8.5x11 page at 1:1 without zooming, so the whole page
        // (canvas + the signature/date/print field overlays, all positioned in the SAME
        // layoutVp coordinate space) scales down together, keeping the fields correctly aligned
        // at any size. The bug was that the width used for that scale wasn't reliably the real
        // viewport: scrollRef.current.clientWidth alone (falling back to a 760px desktop default)
        // could read wider than the actual device screen, rendering an oversized page that forced
        // horizontal scrolling on mobile. window.innerWidth is always the true viewport, so it's
        // used as a hard ceiling regardless of what the container measurement reports.
        const viewportW = Math.min(window.innerWidth, scrollRef.current?.clientWidth || window.innerWidth)
        const colW = Math.min(760, Math.max(240, viewportW - 36))
        // One candidate list PER anchor query (priority order), plus separate lists for the
        // print-name label, the date label, and the "selected suite" placeholder — collected across
        // the whole document first, resolved into a single field set afterward. This two-pass shape
        // is required because a document can carry more than one of the same kind of label (e.g. an
        // Architect AND a Client block each have their own "Date"/"(print)" line): picking anchors by
        // *priority* (Client - before ACCEPTANCE) and printedName/date by *proximity* to whichever
        // anchor actually won avoids grabbing the wrong party's line.
        const anchorLists = anchorQueries.map(() => [])
        const prnList = [], dateList = [], pkgList = []
        for (let n = 1; n <= doc.numPages; n++) {
          if (cancelled) return
          const page = await doc.getPage(n)
          const base = page.getViewport({ scale: 1 })
          const scale = colW / base.width
          const layoutVp = page.getViewport({ scale })
          const renderVp = page.getViewport({ scale: scale * dpr })
          const wrap = document.createElement('div')
          wrap.style.cssText = `position:relative;margin:0 auto 14px;width:${layoutVp.width}px;height:${layoutVp.height}px;box-shadow:0 6px 24px rgba(4,21,24,0.18);border-radius:4px;overflow:hidden;background:#fff`
          const canvas = document.createElement('canvas')
          canvas.width = renderVp.width; canvas.height = renderVp.height
          canvas.style.width = layoutVp.width + 'px'; canvas.style.height = layoutVp.height + 'px'
          canvas.style.display = 'block'
          wrap.appendChild(canvas)
          host.appendChild(wrap)
          await page.render({ canvasContext: canvas.getContext('2d'), viewport: renderVp }).promise
          try {
            const tc = await page.getTextContent()
            for (const it of tc.items) {
              const s = (it.str || '').trim(); if (!s) continue
              const lc = s.toLowerCase()
              const [vx, vy] = layoutVp.convertToViewportPoint(it.transform[4], it.transform[5])
              // Signature-line labels ('Client - X', 'ACCEPTANCE', 'Accepted by (signature)') are
              // short standalone runs, never full sentences — require the anchor at the START of a
              // short item, not merely present anywhere, so long-form agreement prose that happens
              // to contain the same words (e.g. "...or accepted by Client; for any services...")
              // can't be mistaken for the real signature block.
              const isLabel = s.length <= 60
              if (isLabel && !lc.includes('print')) {
                anchorQueries.forEach((q, qi) => { if (lc.startsWith(q.toLowerCase())) anchorLists[qi].push({ wrap, vx, vy, scale }) })
              }
              if (isLabel && lc.includes('print')) prnList.push({ wrap, vx, vy, scale })
              if (lc === 'date') dateList.push({ wrap, vx, vy, scale })
              if (isLabel && lc.includes('to be selected')) pkgList.push({ wrap, vx, vy, scale })
            }
          } catch (_) {}
        }
        // Resolve the signature anchor by PRIORITY (first anchorQueries entry with any match wins),
        // not by document position — otherwise a later, more specific anchor (e.g. 'Client -') loses
        // to an earlier, more generic one (e.g. 'ACCEPTANCE') just because it happens to sit lower on
        // the page. WITHIN whichever query wins, `signerLine` (1-based) then picks that occurrence —
        // on a joint proposal 'Client -' matches once per required signer, in document order (line 1
        // first), so this is what routes each signer to their OWN line instead of both landing on
        // the first one. Clamped to the list's actual length so an out-of-range hint (a proposal
        // that turned out to have only one real line after all) still resolves to something rather
        // than finding nothing.
        let sigA = null
        for (const list of anchorLists) {
          if (list.length) { sigA = list[Math.min(Math.max(signerLine, 1) - 1, list.length - 1)]; break }
        }
        let f = null
        if (sigA) {
          f = { signature: { wrap: sigA.wrap, left: sigA.vx, top: Math.max(2, sigA.vy - 48), w: Math.max(180, 230 * sigA.scale), h: 32 } }
          const d = dateList.find(x => x.wrap === sigA.wrap && Math.abs(x.vy - sigA.vy) < 6)
          if (d) f.date = { wrap: d.wrap, left: d.vx, top: Math.max(2, d.vy - 34), w: Math.max(90, 120 * d.scale), h: 18 }
          // The print/date lines belong to the SAME party block as sigA — scope to the same page and
          // require the print line to sit below sigA's line (viewport y increases downward), within
          // one block's height.
          const p = prnList.find(x => x.wrap === sigA.wrap && x.vy > sigA.vy && (x.vy - sigA.vy) < 70)
          if (p) f.print = { wrap: p.wrap, left: p.vx, top: Math.max(2, p.vy - 34), w: Math.max(180, 230 * p.scale), h: 18 }
          // The "selected suite" placeholder sits above the signature blocks, not scoped per-party —
          // just take the first occurrence on the same page.
          const k = pkgList.find(x => x.wrap === sigA.wrap)
          if (k) f.pkg = { wrap: k.wrap, left: k.vx, top: Math.max(2, k.vy - 12), w: Math.max(160, 220 * k.scale), h: 18 }
        }
        if (!cancelled) { setFields(f); setStatus('ready') }
      } catch (e) { if (!cancelled) setStatus('error') }
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfUrl, signerLine])

  // Paint / update the signature, date, and printed-name fields onto their page wrappers, so the
  // client sees exactly what the executed document will contain (signature drawn/typed, date + name
  // auto-filled). The signature field is interactive; date + name mirror the signing inputs.
  useEffect(() => {
    if (!fields) return
    const esc = s => String(s || '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]))
    const today = (() => { const d = new Date(); const p = n => String(n).padStart(2, '0'); return `${p(d.getMonth() + 1)}/${p(d.getDate())}/${d.getFullYear()}` })()
    const place = (f, cls, decorate) => {
      if (!f || !f.wrap) return
      let el = f.wrap.querySelector('.' + cls)
      if (!el) { el = document.createElement('div'); el.className = cls; el.style.cssText = 'position:absolute;display:flex;align-items:center;border-radius:4px;z-index:2;overflow:hidden'; f.wrap.appendChild(el) }
      el.style.left = f.left + 'px'; el.style.top = f.top + 'px'; el.style.width = f.w + 'px'; el.style.height = f.h + 'px'
      decorate(el)
    }
    place(fields.signature, 'aj-sf-sig', el => {
      el.style.justifyContent = 'center'; el.style.cursor = 'pointer'
      el.onclick = () => { const p = document.getElementById('aj-sign-panel'); if (p) p.scrollIntoView({ behavior: 'smooth', block: 'center' }) }
      if (sig.dataUrl) {
        el.style.border = '1.5px solid rgba(29,158,117,0.7)'; el.style.background = 'rgba(29,158,117,0.06)'
        el.innerHTML = `<img src="${sig.dataUrl}" style="max-width:96%;max-height:88%;object-fit:contain" />`
        try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }) } catch (_) {}
      } else {
        el.style.border = '1.5px dashed #e0a23c'; el.style.background = 'rgba(224,162,60,0.12)'
        el.innerHTML = `<span style="font-family:'DM Sans',sans-serif;font-size:11px;font-weight:600;letter-spacing:0.04em;color:#b8770a;text-transform:uppercase">Sign here</span>`
      }
    })
    place(fields.date, 'aj-sf-date', el => {
      el.style.justifyContent = 'flex-start'; el.style.alignItems = 'flex-end'
      el.innerHTML = `<span style="font-family:'DM Mono',monospace;font-size:13px;color:#1746c8">${today}</span>`
    })
    place(fields.print, 'aj-sf-print', el => {
      el.style.justifyContent = 'flex-start'; el.style.alignItems = 'flex-end'
      el.innerHTML = sig.name ? `<span style="font-family:'DM Sans',sans-serif;font-size:13px;color:#1746c8">${esc(sig.name)}</span>` : ''
    })
    const pkgLabel = lockedPackageLabel || (packageOptions && pkg ? (packageOptions.find(p => p.key === pkg) || {}).label : '')
    place(fields.pkg, 'aj-sf-pkg', el => {
      el.style.justifyContent = 'flex-start'; el.style.alignItems = 'center'
      // Opaque once chosen — covers the printed "— to be selected —" placeholder underneath.
      el.style.background = pkgLabel ? '#fff' : 'transparent'
      el.innerHTML = pkgLabel ? `<span style="font-family:'DM Sans',sans-serif;font-size:13px;font-weight:600;color:#1746c8">${esc(pkgLabel)}</span>` : ''
    })
  }, [fields, sig.dataUrl, sig.name, pkg, packageOptions, lockedPackageLabel])

  // Jump-to-and-highlight — when the signer interacts with a sign-panel input (suite choice, the
  // name/signature capture), scroll the matching field on the document into view and pulse it
  // briefly, so they always see exactly what they're filling in. Mirrors the existing signature-box
  // auto-scroll, generalized to every field key.
  const highlightTimers = useRef({})
  function jumpTo(key, { scroll = true } = {}) {
    const f = fields && fields[key]
    if (!f || !f.wrap) return
    const cls = 'aj-sf-' + key
    const el = f.wrap.querySelector('.' + cls)
    if (!el) return
    if (scroll) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    el.classList.add('aj-sf-pulse')
    clearTimeout(highlightTimers.current[key])
    highlightTimers.current[key] = setTimeout(() => el.classList.remove('aj-sf-pulse'), 1600)
  }
  useEffect(() => () => { Object.values(highlightTimers.current).forEach(clearTimeout) }, [])

  const sideBySide = winW > 900
  const emailValid = !emailCopy || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(copyEmail.trim())
  // Locked mode requires the confirmation checkbox instead of a picked value; a normal multi-option
  // picker requires pkg; a single (or absent) package option needs nothing — it's auto-selected.
  const pkgSatisfied = lockedPackageLabel ? confirmedPkg : (!packageOptions || pkg)
  const canFinish = sig.name && sig.dataUrl && agree && pkgSatisfied && emailValid && !busy

  // "Ready to sign" — the panel opens on a single clear CTA rather than a wall of inputs; clicking it
  // reveals the fields AND jumps the document straight to the first one (suite choice if there is
  // one, otherwise the signature line), so the signer never has to hunt for it by scrolling.
  function startSigning() {
    setStarted(true)
    jumpTo((lockedPackageLabel || packageOptions) ? 'pkg' : 'signature')
  }

  async function finish() {
    if (!canFinish) return
    const selected = lockedPackageLabel || (packageOptions ? (packageOptions.find(p => p.key === pkg) || {}).label : undefined)
    await onSubmit({ signerName: sig.name, signatureDataUrl: sig.dataUrl, method: sig.method, font: sig.font, consent: true, package: selected, email: emailCopy && copyEmail.trim() ? copyEmail.trim() : null })
  }

  return createPortal((
    <div style={S.overlay}>
      <style>{`
        @keyframes aj-sm-spin { to { transform: rotate(360deg); } }
        @keyframes aj-sf-pulse-kf {
          0%, 100% { box-shadow: 0 0 0 0 rgba(23,70,200,0); }
          20% { box-shadow: 0 0 0 5px rgba(23,70,200,0.35); }
          45% { box-shadow: 0 0 0 0 rgba(23,70,200,0); }
        }
        .aj-sf-pulse { animation: aj-sf-pulse-kf 0.8s ease-out 2; }
      `}</style>
      <div style={S.head}>
        <div style={{ minWidth: 0 }}>
          <div style={S.eyebrow}>{subtitle || 'Review & sign'}</div>
          <div style={S.title}>{title}</div>
        </div>
        <button style={S.close} onClick={onClose} aria-label="Close">×</button>
      </div>

      <div style={{ ...S.body, flexDirection: sideBySide ? 'row' : 'column' }}>
        {/* Document */}
        <div ref={scrollRef} style={{ ...S.docPane, ...(sideBySide ? {} : { flex: '0 0 auto', height: '50vh' }) }}>
          {status === 'loading' && <div style={S.note}>Loading the document…</div>}
          {status === 'error' && (
            <div style={S.note}>The document preview couldn’t load. <a href={pdfUrl} target="_blank" rel="noopener" style={S.link}>Open it in a new tab ↗</a></div>
          )}
          <div ref={pagesRef} />
          {status === 'ready' && !fields && (
            <div style={S.note}>Your signature will be applied to the acceptance section of this document.</div>
          )}
        </div>

        {/* Sign panel */}
        <div id="aj-sign-panel" style={{ ...S.signPane, ...(sideBySide ? {} : { width: '100%', flex: '1 1 auto', minHeight: 0, borderLeft: 'none', borderTop: '0.5px solid rgba(7,39,45,0.12)' }) }}>
          <div style={S.signScroll}>
            <a href={pdfUrl} target="_blank" rel="noopener" style={S.link}>Open the full document ↗</a>

            {!started ? (
              <div style={S.startWrap}>
                <div style={S.startTitle}>Ready to sign?</div>
                <p style={S.startBody}>Review the document on the left. When you're ready, start below — we'll take you straight to where you sign.</p>
                <button style={S.finish} onClick={startSigning} disabled={status !== 'ready'}>Ready to sign →</button>
              </div>
            ) : (
              <>
                {lockedPackageLabel ? (
                  <div style={{ margin: '14px 0' }}>
                    <div style={S.fieldLabel}>Confirm the selected suite</div>
                    <div style={S.lockedPkg}>
                      <span style={S.pkgName}>{lockedPackageLabel}</span>
                    </div>
                    <label style={{ ...S.agree, marginTop: 10 }}>
                      <input type="checkbox" checked={confirmedPkg} onChange={e => { setConfirmedPkg(e.target.checked); jumpTo('pkg') }} />
                      <span>I agree with the {lockedPackageLabel} suite selected by the other required signer.</span>
                    </label>
                  </div>
                ) : packageOptions && packageOptions.length > 1 && (
                  <div style={{ margin: '14px 0' }}>
                    <div style={S.fieldLabel}>Select your suite</div>
                    <div style={S.pkgs}>
                      {/* No fee shown here — the proposal document is the only source of truth
                          for pricing. A second, independently-computed number risked drifting
                          out of sync with the real document and showing the client a wrong
                          figure at the moment they're signing. */}
                      {packageOptions.map(p => (
                        <button key={p.key} style={{ ...S.pkg, ...(pkg === p.key ? S.pkgOn : {}) }} onClick={() => { setPkg(p.key); jumpTo('pkg') }}>
                          <span style={S.pkgName}>{p.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div style={S.fieldLabel}>Adopt your signature</div>
                <div onPointerDown={() => { jumpTo('signature'); jumpTo('print', { scroll: false }) }}>
                  <JourneySignaturePad onChange={setSig} />
                </div>

                <label style={S.agree}>
                  <input type="checkbox" checked={agree} onChange={e => setAgree(e.target.checked)} />
                  <span>{consentLabel || 'I agree to sign electronically, and that my electronic signature is the legal equivalent of my handwritten signature and constitutes my acceptance of this document.'}</span>
                </label>

                <label style={{ ...S.agree, marginTop: 8 }}>
                  <input type="checkbox" checked={emailCopy} onChange={e => setEmailCopy(e.target.checked)} />
                  <span>Email me a copy of the signed document</span>
                </label>
                {emailCopy && (
                  <input
                    type="email"
                    placeholder="Your email address"
                    value={copyEmail}
                    onChange={e => setCopyEmail(e.target.value)}
                    style={{ ...S.emailInput, borderColor: copyEmail && !emailValid ? '#c0392b' : 'rgba(7,39,45,0.25)' }}
                  />
                )}

                <button style={{ ...S.finish, ...(canFinish ? {} : S.finishOff), ...(busy ? S.finishBusy : {}) }} onClick={finish} disabled={!canFinish}>
                  {busy ? (
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9 }}>
                      <svg style={{ animation: 'aj-sm-spin 0.85s linear infinite', flexShrink: 0 }} width="15" height="15" viewBox="0 0 15 15" fill="none">
                        <circle cx="7.5" cy="7.5" r="5.75" stroke="rgba(255,255,255,0.28)" strokeWidth="1.6"/>
                        <path d="M7.5 1.75A5.75 5.75 0 0 1 13.25 7.5" stroke="white" strokeWidth="1.6" strokeLinecap="round"/>
                      </svg>
                      Applying signature…
                    </span>
                  ) : 'Apply signature & finish'}
                </button>
                {sig.dataUrl && fields && <div style={S.placedHint}>Your signature, printed name, and date are placed on the document — see the highlighted fields.</div>}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  ), document.body)
}

const S = {
  overlay: { position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(6,22,26,0.62)', backdropFilter: 'blur(3px)', display: 'flex', flexDirection: 'column' },
  head: { flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '16px 22px', background: '#fff', borderBottom: '0.5px solid rgba(7,39,45,0.12)' },
  eyebrow: { fontFamily: '"Montserrat", sans-serif', fontSize: 9, fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#7a9aaa' },
  title: { fontFamily: '"Montserrat", sans-serif', fontSize: 18, fontWeight: 300, color: '#07272D', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  close: { flexShrink: 0, width: 36, height: 36, borderRadius: '50%', border: 'none', background: 'rgba(7,39,45,0.06)', color: '#07272D', fontSize: 22, lineHeight: 1, cursor: 'pointer' },
  body: { flex: 1, display: 'flex', minHeight: 0 },
  // overflowX hidden is a hard guarantee against page-level horizontal scroll — belt-and-suspenders
  // alongside the colW viewport clamp above, in case any measurement is ever still off.
  docPane: { flex: 1, minWidth: 0, overflowY: 'auto', overflowX: 'hidden', padding: '20px 16px', background: '#eef2f3' },
  note: { fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: '#5a7884', textAlign: 'center', padding: '24px 12px' },
  signPane: { flexShrink: 0, width: 'min(420px, 42vw)', maxWidth: '100%', background: '#fff', overflowY: 'auto' },
  signScroll: { padding: '20px 22px 28px' },
  fieldLabel: { fontFamily: '"Montserrat", sans-serif', fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(7,39,45,0.5)', margin: '10px 0 8px' },
  startWrap: { marginTop: 28, textAlign: 'center' },
  startTitle: { fontFamily: '"Montserrat", sans-serif', fontSize: 17, fontWeight: 300, color: '#07272D', marginBottom: 10 },
  startBody: { fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: '#5a7884', lineHeight: 1.6, margin: '0 0 20px' },
  pkgs: { display: 'flex', flexDirection: 'column', gap: 8 },
  pkg: { display: 'flex', alignItems: 'center', textAlign: 'left', background: '#fff', border: '0.5px solid rgba(7,39,45,0.2)', borderRadius: 8, padding: '10px 12px', cursor: 'pointer' },
  pkgOn: { borderColor: '#1D9E75', boxShadow: '0 0 0 2px rgba(29,158,117,0.25)' },
  pkgName: { fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: '#07272D' },
  // Read-only counterpart to `pkg` — the second signer sees the already-chosen suite but can't
  // click it into something else, so no cursor:pointer and a slightly muted fill vs. the picker.
  lockedPkg: { display: 'flex', alignItems: 'center', textAlign: 'left', background: 'rgba(7,39,45,0.04)', border: '0.5px solid rgba(7,39,45,0.2)', borderRadius: 8, padding: '10px 12px' },
  agree: { display: 'flex', gap: 9, alignItems: 'flex-start', fontSize: 12, color: '#3a5260', lineHeight: 1.5, margin: '16px 0 4px', cursor: 'pointer' },
  finish: { width: '100%', fontFamily: '"DM Sans", sans-serif', fontSize: 13.5, fontWeight: 600, color: '#fff', background: '#07272D', border: 'none', borderRadius: 6, padding: '12px', cursor: 'pointer', marginTop: 14 },
  finishOff: { opacity: 0.4, cursor: 'not-allowed' },
  finishBusy: { opacity: 0.82, cursor: 'default' },
  placedHint: { fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, color: '#1D9E75', marginTop: 10, lineHeight: 1.5 },
  link: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: '#1a6fa8', textDecoration: 'none' },
  emailInput: { width: '100%', fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: '#07272D', padding: '9px 12px', border: '0.5px solid rgba(7,39,45,0.25)', borderRadius: 6, background: '#fff', boxSizing: 'border-box', marginTop: 6, outline: 'none' },
}
